import { ERROR_CODES } from '@dealstack/shared/lib/common/errors';
import { z } from 'zod';
import type {
	TransactionType,
	TransactionThresholdExpressedAs,
	SecurityParticipationThresholdBasis,
} from '@dealstack/prisma-client';
import { TransactionTitle, TransactionWithSecurityTct } from '@dealstack/shared/lib/generated';

//these validations are used in frontend and in backend so we can't use neither genereted enum or prisma enum
const TransactionTypes: Record<TransactionType, TransactionType> = {
	TRANSFER: 'TRANSFER',
	ISSUE: 'ISSUE',
	REDEMPTION: 'REDEMPTION',
	CANCELLATION: 'CANCELLATION',
	ISSUE_SPLIT: 'ISSUE_SPLIT',
	REDEMPTION_REVERSE_SPLIT: 'REDEMPTION_REVERSE_SPLIT',
	TRANSFER_CONVERSION: 'TRANSFER_CONVERSION',
	SHAREHOLDER_CONTRIBUTION: 'SHAREHOLDER_CONTRIBUTION',
	REDUCTION_IN_SHARE_CAPITAL: 'REDUCTION_IN_SHARE_CAPITAL',
	CONVERSION_SHARE_CAPITAL: 'CONVERSION_SHARE_CAPITAL',
	DIVIDEND: 'DIVIDEND',
	VESTING_EVENT: 'VESTING_EVENT',
};

/**
 * Transaction types that are emitted internally by the pipeline and must
 * never be accepted on create/update endpoints, surfaced in cap-table UI,
 * or referenced from user-facing copy. Currently: `VESTING_EVENT` (emitted
 * by `split_transactions`).
 *
 * Adding a new internal-only type here automatically narrows
 * {@link UserSubmittableTransactionType} via the `typeof […][number]` derivation.
 */
export const INTERNAL_ONLY_TRANSACTION_TYPES = ['VESTING_EVENT'] as const;

export type InternalOnlyTransactionType = (typeof INTERNAL_ONLY_TRANSACTION_TYPES)[number];

/** TransactionType minus the internal-only set — the user-facing subset. */
export type UserSubmittableTransactionType = Exclude<TransactionType, InternalOnlyTransactionType>;

const UserSubmittableTransactionTypes = Object.fromEntries(
	Object.entries(TransactionTypes).filter(
		([key]) => !INTERNAL_ONLY_TRANSACTION_TYPES.includes(key as InternalOnlyTransactionType)
	)
) as Record<UserSubmittableTransactionType, UserSubmittableTransactionType>;

const TransactionThresholdExpressedAsTypes: Record<
	TransactionThresholdExpressedAs,
	TransactionThresholdExpressedAs
> = {
	TOTAL_VALUE_IN_TIER: 'TOTAL_VALUE_IN_TIER',
	VALUE_PER_UNIT: 'VALUE_PER_UNIT',
};

const SecurityParticipationThresholdBasisTypes: Record<
	SecurityParticipationThresholdBasis,
	SecurityParticipationThresholdBasis
> = {
	TOTAL_SOURCES_FOR_ISSUER: 'TOTAL_SOURCES_FOR_ISSUER',
	TOTAL_PROCEEDS_WITHIN_TIER: 'TOTAL_PROCEEDS_WITHIN_TIER',
};

export const SplitTransactionSchema = z.object({
	id: z.string(),
	units1: z.number().nonnegative(),
	cost1: z.number().nonnegative(),
	units2: z.number().nonnegative(),
	cost2: z.number().nonnegative(),
});

export type SplitTransaction = z.infer<typeof SplitTransactionSchema>;

export const PairTransactionSchema = z.object({
	transactionOneId: z.string(),
	transactionTwoId: z.string(),
});

export type PairTransaction = z.infer<typeof PairTransactionSchema>;

const validateTransactionDirection = (
	{
		type,
		sourceId,
		destinationId,
		securityPersonId,
		participationThresholdExpressedAs,
		participationThreshold,
		title,
	}: {
		type?: TransactionType | null;
		sourceId: string | null;
		destinationId: string | null;
		securityId: string;
		securityPersonId?: string;
		units?: number;
		cost?: number;
		participationThresholdExpressedAs?: TransactionThresholdExpressedAs | null;
		participationThreshold?: number;
		title?: TransactionTitle | null;
	},
	ctx: z.RefinementCtx,
	transaction?: TransactionWithSecurityTct
) => {
	if (!sourceId) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			params: {
				businessCode: ERROR_CODES.PROJECT.CAP.TRANSACTIONS[120201],
			},
			message: 'Source is required',
			path: ['sourceId'],
		});
	}
	if (!destinationId && type !== TransactionTypes.CANCELLATION) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			message: 'Destination is required',
			params: {
				businessCode: ERROR_CODES.PROJECT.CAP.TRANSACTIONS[120202],
			},
			path: ['destinationId'],
		});
	}

	if (!type) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			message: 'Transaction type is required',
			params: {
				businessCode: ERROR_CODES.PROJECT.CAP.TRANSACTIONS[120207],
			},
			path: ['type'],
		});
	}

	const security = transaction?.security;

	if (type === TransactionTypes.ISSUE && security?.residueParticipationThreshold) {
		if (
			!participationThresholdExpressedAs &&
			security?.residueParticipationThresholdBasis ===
				SecurityParticipationThresholdBasisTypes.TOTAL_PROCEEDS_WITHIN_TIER
		) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'Participation threshold expressed as is required',
				params: {
					businessCode: ERROR_CODES.PROJECT.CAP.TRANSACTIONS[120219],
				},
				path: ['participationThresholdExpressedAs'],
			});
		}

		if (security?.residueParticipationThreshold && typeof participationThreshold !== 'number') {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'Participation threshold is required',
				params: {
					businessCode: ERROR_CODES.PROJECT.CAP.TRANSACTIONS[120220],
				},
				path: ['participationThreshold'],
			});
		}
	}

	switch (type) {
		case TransactionTypes.TRANSFER: {
			if (sourceId !== destinationId) {
				return;
			}
			if (!sourceId && !destinationId) {
				return;
			}
			// CAP-1976: the transfer-split's counterpart leg is a deliberate self-transfer, a
			// single-title row linked by interestPairId whose source and destination are the same
			// holder by design, so editing IT must not trip this rule.
			//
			// The exemption is keyed on the STORED row already being that self-transfer. Keyed on
			// "paired and single-title" alone it could not tell the counterpart from the visible
			// leg, so it also let the visible leg BECOME a self-transfer: picking the seller as the
			// buyer saved with a success toast, and the row then matched applyGhostLegExclusion and
			// was hidden by the very rule that hides the counterpart. The pair vanished from the
			// grid, the holder's units vanished from the cap table, and the only way back was the
			// detail-page URL.
			const storedIsCounterpart =
				!!transaction?.sourceId && transaction.sourceId === transaction.destinationId;
			if (
				storedIsCounterpart &&
				transaction?.interestPairId &&
				title &&
				title !== TransactionTitle.BENEFICIAL_INTEREST_AND_LEGAL_TITLE
			) {
				return;
			}
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'Source and destination cannot be the same',
				params: {
					businessCode: ERROR_CODES.PROJECT.CAP.TRANSACTIONS[120203],
				},
				path: ['destinationId'],
			});
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'Source and destination cannot be the same',
				params: {
					businessCode: ERROR_CODES.PROJECT.CAP.TRANSACTIONS[120203],
				},
				path: ['sourceId'],
			});
			break;
		}
		case TransactionTypes.CONVERSION_SHARE_CAPITAL: {
			// Allows source == destination
			break;
		}

		case TransactionTypes.SHAREHOLDER_CONTRIBUTION:
		case TransactionTypes.ISSUE: {
			if (!sourceId && !destinationId) {
				return;
			}
			if (sourceId !== securityPersonId && sourceId && securityPersonId) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: `Seller ${sourceId} must be the issuing entity of security ${securityPersonId}`,
					path: ['sourceId'],
					params: {
						businessCode: ERROR_CODES.PROJECT.CAP.TRANSACTIONS[120205],
					},
				});
			}

			break;
		}
		case TransactionTypes.REDUCTION_IN_SHARE_CAPITAL:
		case TransactionTypes.DIVIDEND:
		case TransactionTypes.REDEMPTION: {
			if (!sourceId && !destinationId) {
				return;
			}

			if (destinationId !== securityPersonId && destinationId && securityPersonId) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'Buyer must be the issuing entity of this security',
					path: ['destinationId'],
					params: {
						businessCode: ERROR_CODES.PROJECT.CAP.TRANSACTIONS[120204],
					},
				});
			}
			break;
		}
		case TransactionTypes.CANCELLATION: {
			if (!sourceId) {
				return;
			}

			break;
		}
		default:
			if (!sourceId && !destinationId) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'Insufficient data',
					path: ['type'],
					params: {
						businessCode: ERROR_CODES.PROJECT.CAP.TRANSACTIONS[120206],
					},
				});
			}
	}
};

const TransactionShapeObject = z.object({
	type: z.nativeEnum(UserSubmittableTransactionTypes).nullish(),
	date: z.date({ invalid_type_error: 'The mandatory field is empty' }),
	securityId: z.string({ invalid_type_error: 'The mandatory field is empty' }),
	securityPersonId: z.string({ invalid_type_error: 'The mandatory field is empty' }).optional(),
	units: z.number().nonnegative().optional(),
	cost: z.number().nonnegative().optional(),
	sourceId: z.string({
		required_error: 'SOURCE_ENTITY_REQUIRED',
		invalid_type_error: 'SOURCE_ENTITY_REQUIRED',
	}),
	destinationId: z
		.string({
			required_error: 'DESTINATION_ENTITY_REQUIRED',
			invalid_type_error: 'DESTINATION_ENTITY_REQUIRED',
		})
		.nullable(),
	fromMipId: z.string().nullable().optional(),
	soldForId: z.string().nullable().optional(),
	boughtForId: z.string().nullable().optional(),
	toMipId: z.string().nullable().optional(),
	participationThresholdExpressedAs: z.nativeEnum(TransactionThresholdExpressedAsTypes).nullish(),
	participationThreshold: z.number().nonnegative().optional(),
	conversionTargetSecurityId: z.string().nullable().optional(),
	// CAP-1975: nominee legal/beneficial interest for this transaction. Optional — non-nominee
	// transactions omit it, and the backend treats an omitted title as "Both". Kept in the shared
	// shape (rather than passed loosely) so react-hook-form's zodResolver preserves it on submit.
	title: z.nativeEnum(TransactionTitle).nullable().optional(),
});

const TransactionDirectionShapeStrict = (transaction?: TransactionWithSecurityTct) =>
	TransactionShapeObject.superRefine((data, ctx) =>
		validateTransactionDirection(data, ctx, transaction)
	);

export const CreateTransactionShapeStrict = TransactionDirectionShapeStrict;
export type CreateTransactionDto = z.infer<typeof TransactionShapeObject>;

export const UpdateTransactionShapeStrict = (transaction?: TransactionWithSecurityTct) =>
	CreateTransactionShapeStrict(transaction).and(z.object({ id: z.string() }));
export type UpdateTransactionDto = z.infer<ReturnType<typeof UpdateTransactionShapeStrict>>;
